import Grid from "@mui/material/Grid";
import {
  selectClickedSensor, selectMetricData,
  selectSelectedSensors, selectSensorGeojsonData,
  selectSensorLocations, selectSensorParameter,
  setClickedSensor
} from "../../../store/slices/sensorDataSlice";
import {FaArrowLeft} from "react-icons/fa";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import {FaCheckCircle} from "react-icons/fa";
import {FaLink} from "react-icons/fa";
import {LastUpdatedDisplay} from "../LastUpdatedDisplay";
import {FaChevronCircleLeft} from "react-icons/fa";
import {FaChevronCircleRight} from "react-icons/fa";
import {SensorValueDisplay} from "../SensorValueDisplay";
import {SensorBarChart} from "../SensorBarChart";
import {getLocation, getMetadata, LButton, LHeader, SensorValueLabelTooltip} from "../common";
import {useSearchParams} from "react-router-dom";
import {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import useMediaQuery from "@mui/material/useMediaQuery";

const CopyLinkToClipboard = () => {
  const [linkCopied, setLinkCopied] = useState(false);
  const handleTooltipOpen = () => {
    navigator.clipboard.writeText(window.location.href);
    setLinkCopied(true);
  };
  const handleTooltipClose = () => {
    setLinkCopied(false);
  };

  return(
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <div>
        <Tooltip
          open={linkCopied}
          onClose={handleTooltipClose}
          onOpen={handleTooltipOpen}
          placement={'left'}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          arrow={true}
          title={<Grid container alignItems={'center'}>
            <FaCheckCircle style={{ marginRight: '.5rem' }} />
            Link copied
          </Grid>}
          slotProps={{
            popper: {
              disablePortal: true,
            },
          }}
          slots={{ transition: Zoom }}
        >
          <LButton onClick={handleTooltipOpen}>
            <FaLink style={{ width: '18px', height: '18px' }} />
          </LButton>
        </Tooltip>
      </div>
    </ClickAwayListener>
  );
}

export const ClickedSensorPanel = ({ push, pop }) => {
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();

  const largeScreen = useMediaQuery('(min-width: 600px)');

  const locations = useSelector(selectSensorLocations);
  const selectedSensors = useSelector(selectSelectedSensors);
  const clickedSensor = useSelector(selectClickedSensor);
  const selectedParameter = useSelector(selectSensorParameter);
  const metricData = useSelector(selectMetricData);
  const geojsonData = useSelector(selectSensorGeojsonData);

  const clickedLocation = getLocation(locations, clickedSensor);

  // Grab our previously-fetched data to determine some stats
  // TODO: we can do better for this logic, but for now this should work alright
  const {latestValue, latestRow} = getMetadata({parameter: selectedParameter, datasourceId: clickedSensor, geojsonData});

  // Page backward by one, if our clicked sensor is in the list of selected sensors
  const prevSensor = () => {
    const index = selectedSensors?.indexOf(clickedSensor);
    if (index === -1) { return; }
    const newIndex = index === 0 ? selectedSensors?.length - 1 : index - 1;
    dispatch(setClickedSensor(selectedSensors[newIndex]));
  };

  // Page forward by one, if our clicked sensor is in the list of selected sensors
  const nextSensor = () => {
    const index = selectedSensors?.indexOf(clickedSensor);
    if (index === -1) { return; }
    const newIndex = index === (selectedSensors?.length - 1) ? 0 : index + 1;
    dispatch(setClickedSensor(selectedSensors[newIndex]));
  };

  return(
    <>
      <Grid container spacing={0} alignItems={'center'} marginTop={'2rem'}>
        <Grid size={2}>
          <LButton onClick={() => dispatch(setClickedSensor()) && setSearchParams({})} >
            <FaArrowLeft style={{ width: '15px', height: '15px' }} />
          </LButton>
        </Grid>

        <Grid size={8}>
          <LHeader>{clickedLocation?.name}</LHeader>
        </Grid>

        <Grid size={2} alignItems={'end'}>
          <CopyLinkToClipboard />
        </Grid>
      </Grid>

      <Grid container spacing={0} alignItems={'center'}>
        <Grid offset={2} size={10}>
          <LastUpdatedDisplay datasourceId={clickedSensor}></LastUpdatedDisplay>
        </Grid>
      </Grid>

      {selectedSensors?.includes(clickedSensor) && <Grid container spacing={0} justifyContent={"space-between"}>
        <Grid size={6}>
          <LButton style={{ position: 'absolute', zIndex:1000, left: largeScreen ? '-2rem' : '.5rem', marginTop: largeScreen ? '2rem' : '4rem', fontSize: '28px',  width: '36px', height: '36px' }}
                   onClick={() => prevSensor()}>
            <FaChevronCircleLeft style={{ border: '2px solid white', borderRadius: '100px', backgroundColor: 'white',color: 'rgba(0, 88, 153, 1)' }} />
          </LButton>
        </Grid>
        <Grid size={6}>
          <LButton style={{ position: 'absolute', zIndex:1000, right: largeScreen ? '-2rem' : '.5rem', marginTop: largeScreen ? '2rem' : '4rem', fontSize: '28px',  width: '36px', height: '36px' }}
                   onClick={() => nextSensor()}>
            <FaChevronCircleRight style={{ border: '2px solid white', borderRadius: '100px', backgroundColor: 'white',color: 'rgba(0, 88, 153, 1)' }} />
          </LButton>
        </Grid>
      </Grid>}

      {latestRow && Object.keys(latestRow)?.length > 2 && <Grid container spacing={0} alignItems={'center'}>
        <Grid offset={2} size={8}>
          <SensorValueDisplay value={latestValue}></SensorValueDisplay>
        </Grid>
        <Grid size={2} onClick={() => push(['Color Coding Air Quality'])}><SensorValueLabelTooltip /></Grid>
      </Grid>}

      <Grid container spacing={0} justifyContent={'space-between'} alignItems={'center'}>
        <Grid size={10}>
          {latestRow && Object.keys(latestRow)?.length <= 2 && <>Loading, Please Wait...</>}
        </Grid>
      </Grid>

      <Grid container alignItems={'center'}>
        <Grid offset={1} size={11}>
          <SensorBarChart pageSize={30}
                          averageType={'hour'}
                          context={'recent'}
                          selectedParameter={selectedParameter}
                          metricData={metricData?.filter(d => d.type === 'hour')?.map(r =>
                            ({ type: r.type, date: r.date, [selectedParameter]: r[clickedSensor] })
                          )} />
        </Grid>
      </Grid>

      <Grid size={12} textAlign={'center'}>
        <LButton variant={'outlined'} onClick={() => push(['Details'])}>Explore Historical Trends & Download &rarr;</LButton>
      </Grid>
    </>
  );
}
